import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { memo, useMemo } from 'react';
function formatCurrency(value) {
    if (value === 0) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${value.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
    return `$${value.toFixed(2)}`;
}
function collectOverrides(clin) {
    const results = [];
    const scanLines = (lines)=>{
        for (const line of lines){
            for (const [name, param] of Object.entries(line.parameters)){
                if (param.metadata?.override) {
                    results.push({
                        paramName: name,
                        label: param.label,
                        override: param.metadata.override
                    });
                }
            }
        }
    };
    if (clin.laborStructure.type === 'direct' || clin.laborStructure.type === 'government_defined') {
        for (const group of clin.laborStructure.groups)scanLines(group.lines);
    } else if (clin.laborStructure.type === 'tiered') {
        for (const tier of clin.laborStructure.tiers)scanLines(tier.group.lines);
    }
    return results;
}
function CLINContext({ clin }) {
    const hasPeriodTotals = Object.keys(clin.periodTotals).length > 0;
    const overrides = collectOverrides(clin);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-2",
                        children: [
                            "CLIN ",
                            clin.clinNumber
                        ]
                    }),
                    clin.metadata.title && /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-700 mb-2",
                        children: clin.metadata.title
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex flex-wrap gap-1.5",
                        children: [
                            clin.metadata.contractType && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-[10px]",
                                children: clin.metadata.contractType
                            }),
                            clin.metadata.periodName && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-[10px] bg-blue-50 text-blue-700 border-blue-200",
                                children: clin.metadata.periodName
                            }),
                            clin.metadata.isOptionalTask && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-[10px] bg-amber-50 text-amber-700 border-amber-200",
                                children: "Optional Task"
                            }),
                            clin.metadata.isNsp && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-[10px] bg-gray-100 text-gray-500 border-gray-300",
                                children: "NSP"
                            }),
                            clin.metadata.costType && /*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-[10px]",
                                children: clin.metadata.costType
                            })
                        ]
                    })
                ]
            }),
            clin.metadata.popMonths && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: "Period of Performance"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-gray-700",
                        children: [
                            clin.metadata.popMonths,
                            " months"
                        ]
                    }),
                    clin.volumeMethod.type === 'exercise_based' && /*#__PURE__*/ _jsx("div", {
                        className: "text-xs text-gray-400 mt-0.5",
                        children: Object.entries(clin.volumeMethod.monthsPerPeriod).map(([period, months])=>/*#__PURE__*/ _jsxs("div", {
                                children: [
                                    period,
                                    ": ",
                                    months,
                                    " months",
                                    months !== 12 && /*#__PURE__*/ _jsx("span", {
                                        className: "text-amber-500 ml-1",
                                        children: "(non-standard)"
                                    })
                                ]
                            }, period))
                    })
                ]
            }),
            clin.metadata.correspondingBaseClin && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: "Base CLIN"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm text-gray-700",
                        children: clin.metadata.correspondingBaseClin
                    })
                ]
            }),
            clin.metadata.nteCeiling != null && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: "NTE Ceiling"
                    }),
                    /*#__PURE__*/ _jsx("p", {
                        className: "text-sm font-medium text-gray-900",
                        children: formatCurrency(clin.metadata.nteCeiling)
                    })
                ]
            }),
            overrides.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-2",
                        children: [
                            "Overrides (",
                            overrides.length,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-2",
                        children: overrides.map((ov, i)=>/*#__PURE__*/ _jsxs("div", {
                                className: "text-xs bg-orange-50 rounded p-2 border border-orange-200",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "font-medium text-orange-700",
                                        children: ov.label
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "text-gray-500 mt-0.5",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "line-through",
                                                children: ov.override.originalValue
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "mx-1",
                                                children: "→"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "text-gray-700",
                                                children: "current"
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-gray-400 mt-0.5 italic",
                                        children: ov.override.reason
                                    })
                                ]
                            }, i))
                    })
                ]
            }),
            hasPeriodTotals && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-2",
                        children: "Cost Breakdown"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-2",
                        children: Object.entries(clin.periodTotals).map(([period, pt])=>/*#__PURE__*/ _jsxs("div", {
                                className: "text-xs space-y-0.5",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "font-medium text-gray-600",
                                        children: period
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between text-gray-500",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Labor"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "tabular-nums",
                                                children: formatCurrency(pt.laborCost)
                                            })
                                        ]
                                    }),
                                    pt.indirectCost > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between text-gray-500",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Indirect"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "tabular-nums",
                                                children: formatCurrency(pt.indirectCost)
                                            })
                                        ]
                                    }),
                                    pt.feeCost > 0 && /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between text-gray-500",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Fee"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "tabular-nums",
                                                children: formatCurrency(pt.feeCost)
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex justify-between font-medium text-gray-700 pt-0.5 border-t border-gray-100",
                                        children: [
                                            /*#__PURE__*/ _jsx("span", {
                                                children: "Total"
                                            }),
                                            /*#__PURE__*/ _jsx("span", {
                                                className: "tabular-nums",
                                                children: formatCurrency(pt.totalCost)
                                            })
                                        ]
                                    })
                                ]
                            }, period))
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex justify-between text-sm font-semibold text-gray-900 pt-2 mt-2 border-t border-gray-200",
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: "Grand Total"
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                className: "tabular-nums",
                                children: formatCurrency(clin.grandTotal)
                            })
                        ]
                    })
                ]
            })
        ]
    });
}
function SharedTierContext({ configId, sharedTierConfigs, clinPricing }) {
    const config = sharedTierConfigs.find((c)=>`shared-tier-${c.id}` === configId);
    if (!config) return null;
    // Find CLINs referencing this tier
    const referencingClins = clinPricing.filter((clin)=>{
        if (clin.laborStructure.type !== 'tiered_shared') return false;
        return clin.laborStructure.tierRefs.some((ref)=>ref.sharedTierConfigId === config.id);
    });
    const totalFtes = config.group.lines.reduce((s, l)=>s + (l.fteCount ?? 0), 0);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-2",
                        children: "Shared Tier"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm font-semibold text-gray-900",
                        children: [
                            config.tierLabel,
                            " (Tier ",
                            config.tierNumber,
                            ")"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: "Composition"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-gray-700",
                        children: [
                            config.group.lines.length,
                            " roles, ",
                            totalFtes,
                            " FTEs"
                        ]
                    }),
                    config.fteConstraints.min != null && /*#__PURE__*/ _jsxs("p", {
                        className: "text-xs text-gray-400",
                        children: [
                            "Constraint: ",
                            config.fteConstraints.min,
                            config.fteConstraints.max != null && config.fteConstraints.max !== config.fteConstraints.min ? `–${config.fteConstraints.max}` : '',
                            ' ',
                            "FTEs"
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsxs("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: [
                            "Referenced By (",
                            referencingClins.length,
                            " CLINs)"
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "space-y-0.5",
                        children: referencingClins.map((clin)=>/*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-gray-600",
                                children: [
                                    "CLIN ",
                                    clin.clinNumber,
                                    clin.metadata.title && /*#__PURE__*/ _jsxs("span", {
                                        className: "text-gray-400 ml-1",
                                        children: [
                                            "— ",
                                            clin.metadata.title
                                        ]
                                    })
                                ]
                            }, clin.clinNumber))
                    })
                ]
            })
        ]
    });
}
function RateCardContext({ pricing }) {
    const rateCard = pricing.rateCard;
    const avgRate = useMemo(()=>{
        if (!rateCard) return 0;
        let sum = 0;
        let count = 0;
        for (const cat of rateCard.categories){
            const defaultRate = cat.rates.default ?? Object.values(cat.rates)[0];
            if (defaultRate?.value) {
                sum += defaultRate.value;
                count++;
            }
        }
        return count > 0 ? sum / count : 0;
    }, [
        rateCard
    ]);
    if (!rateCard) return null;
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-4",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-2",
                        children: "Rate Card"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm text-gray-700",
                        children: [
                            rateCard.categories.length,
                            " categories (",
                            rateCard.rateType,
                            ")"
                        ]
                    })
                ]
            }),
            avgRate > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: "Average Rate"
                    }),
                    /*#__PURE__*/ _jsxs("p", {
                        className: "text-sm font-medium text-gray-900",
                        children: [
                            "$",
                            avgRate.toFixed(2),
                            "/hr"
                        ]
                    })
                ]
            }),
            rateCard.locationVariants && rateCard.locationVariants.length > 0 && /*#__PURE__*/ _jsxs("div", {
                children: [
                    /*#__PURE__*/ _jsx("h4", {
                        className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-1",
                        children: "Location Variants"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex gap-1",
                        children: rateCard.locationVariants.map((loc)=>/*#__PURE__*/ _jsx(Badge, {
                                variant: "outline",
                                className: "text-[10px]",
                                children: loc === 'on_site' ? 'On-Site' : 'Off-Site'
                            }, loc))
                    })
                ]
            })
        ]
    });
}
function OverviewContext({ clinPricing }) {
    const totalValue = clinPricing.reduce((s, c)=>s + c.grandTotal, 0);
    const laborCount = clinPricing.filter((c)=>c.laborStructure.type !== 'none').length;
    return /*#__PURE__*/ _jsx("div", {
        className: "space-y-4",
        children: /*#__PURE__*/ _jsxs("div", {
            children: [
                /*#__PURE__*/ _jsx("h4", {
                    className: "text-xs font-medium text-gray-500 uppercase tracking-wider mb-2",
                    children: "Proposal Summary"
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-1",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Total Value"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "font-semibold text-gray-900 tabular-nums",
                                    children: formatCurrency(totalValue)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Total CLINs"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-700",
                                    children: clinPricing.length
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "flex justify-between text-sm",
                            children: [
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-500",
                                    children: "Labor CLINs"
                                }),
                                /*#__PURE__*/ _jsx("span", {
                                    className: "text-gray-700",
                                    children: laborCount
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
export const ContextPanel = /*#__PURE__*/ memo(function ContextPanel({ selectedNodeId, pricing, clinPricing, sharedTierConfigs }) {
    // Find the CLIN for this node
    const selectedClin = useMemo(()=>clinPricing.find((c)=>c.clinNumber === selectedNodeId), [
        clinPricing,
        selectedNodeId
    ]);
    if (selectedNodeId === 'overview') {
        return /*#__PURE__*/ _jsx(OverviewContext, {
            clinPricing: clinPricing
        });
    }
    if (selectedNodeId === 'rate-card') {
        return /*#__PURE__*/ _jsx(RateCardContext, {
            pricing: pricing
        });
    }
    if (selectedNodeId === 'indirect') {
        const indirectConfig = pricing.globalIndirectConfig;
        return /*#__PURE__*/ _jsxs("div", {
            className: "space-y-3",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "text-xs text-gray-400",
                    children: "Configure indirect rate pools to compute wrap rates applied to labor costs."
                }),
                indirectConfig && indirectConfig.pools.length > 0 && /*#__PURE__*/ _jsxs("div", {
                    className: "text-xs text-gray-500",
                    children: [
                        indirectConfig.pools.length,
                        " pool(s) configured"
                    ]
                })
            ]
        });
    }
    if (selectedNodeId.startsWith('shared-tier-')) {
        return /*#__PURE__*/ _jsx(SharedTierContext, {
            configId: selectedNodeId,
            sharedTierConfigs: sharedTierConfigs,
            clinPricing: clinPricing
        });
    }
    if (selectedClin) {
        return /*#__PURE__*/ _jsx(CLINContext, {
            clin: selectedClin
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        className: "text-xs text-gray-400 py-4",
        children: "Select a CLIN or configuration item to see details."
    });
});
